import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

import type { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import rangeByDay from 'modules/timeoff/lib/rangeByDay'
import type BlockedPeriodModel from 'modules/timeoff/store/models/BlockedPeriods/BlockedPeriod'

import insertToTree from './insertToTree'

const getRange = (blockedPeriod: BlockedPeriodModel, year: number) => {
  const startDate = moment.max([
    stringToDate(blockedPeriod.attributes.start_on, 'backend'),
    moment([year - 1]),
  ])
  const endDate = moment.min([
    stringToDate(blockedPeriod.attributes.finish_on, 'backend'),
    moment([year + 1]).endOf('year'),
  ])
  return rangeByDay(startDate, endDate)
}

function reduceOverlappingBlockedPeriods(
  blockedPeriods: BlockedPeriodModel[]
): BlockedPeriodModel[] {
  const sorted = blockedPeriods.sort((a, b) =>
    a.attributes.start_on < b.attributes.start_on
      ? -1
      : a.attributes.start_on > b.attributes.start_on
        ? 1
        : 0
  )

  const result: BlockedPeriodModel[] = []
  for (let i = 0; i < sorted.length; i++) {
    const last = result[result.length - 1]
    const current = sorted[i] as BlockedPeriodModel
    if (!last) {
      result.push(sorted[i] as BlockedPeriodModel)
    } else {
      const lastFinish = last.attributes.finish_on
      const currentStart = current.attributes.start_on
      if (lastFinish < currentStart) {
        result.push(current)
      } else {
        const currentFinish = current.attributes.finish_on
        if (lastFinish < currentFinish) {
          last.attributes.finish_on = currentFinish
        }
      }
    }
  }

  return result
}

class BlockedPeriodsToDateTree {
  blockedPeriods: BlockedPeriodModel[]
  memo: DaysTree

  constructor(blockedPeriods: BlockedPeriodModel[]) {
    this.blockedPeriods = blockedPeriods
    this.memo = {}
  }

  munch(year: number) {
    /*  We create this function to avoid N+1 problems when there are several blocked periods.
      Since we can have overlapping blocked periods for the same employee, we could add several times th
      same day with the same payload as second argument, ''. Insert to tree removes duplicates, so
      in the end we would get the same result as if we had only one blocked period.
    */
    reduceOverlappingBlockedPeriods(this.blockedPeriods).forEach(
      (blockedPeriod: BlockedPeriodModel) => {
        getRange(blockedPeriod, year).forEach((day) => {
          insertToTree(this.memo, '')(day)
        })
      }
    )

    return this.memo
  }
}

export default BlockedPeriodsToDateTree
